// src/components/StoryPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { fetchStory } from "../api";
import { IMAGE_BASE_URL, THUMB_BASE_URL } from "../config";
 
const DEFAULT_THUMBNAIL = "/default-thumb.jpg";

function base64Decode(base64String) {
  if (!base64String) return "";

  try {
    const decoded = atob(base64String);
    return decodeURIComponent(
      decoded
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
  } catch (e) {
    console.error("Failed to decode Base64 story:", e);
    return "";
  }
}

function formatDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("mr-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// normalize tags for story page
function normalizeTags(raw) {
  if (!raw) return [];

  if (Array.isArray(raw)) raw = raw.join(" ");

  let text = String(raw).trim();

  // replace common separators with space
  text = text.replace(/[#|,/;:()\[\]{}'"“”‘’।\/\-–_+\n\r\t]+/g, " ");
  text = text.replace(/\s+/g, " ");

  const parts = text
    .split(" ")
    .map((t) => t.trim())
    .filter(Boolean);

  const seen = new Set();
  const out = [];
  for (const p of parts) {
    const clean = p.replace(/^#+/, "").trim();
    if (!clean) continue;
    const key = clean.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean);
  }
  return out;
}

const StoryPage = () => {
  const { slug } = useParams();
  const location = useLocation();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [showImagePopup, setShowImagePopup] = useState(false);

  // from list page
  const relatedStories = location.state?.relatedStories || [];
  const category = location.state?.category || "latestNews";

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const data = await fetchStory(slug);
        if (!cancelled) {
          setStory(data);
          setImgSrc(data.image || "");
          document.title = data.title || "News Story";
        }
      } catch (e) {
        console.error("Error fetching story", e);
        if (!cancelled) setErr("ही बातमी सध्या उपलब्ध नाही.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const onImgError = () => setImgSrc(null);

  if (loading) return <div className="loading">बातमी लोड होत आहे...</div>;
  if (err) return <div className="error-message">{err}</div>;
  if (!story) return null;

  const decodedHtml = base64Decode(story.story);
  const fullImageUrl = imgSrc ? `${IMAGE_BASE_URL}${imgSrc}` : "";

  // ✅ no hook, just a normal computed value
  const tags = normalizeTags(story.tags);

  return (
    <article className="story-page">
      <h1 className="story-title">{story.title}</h1>
      <div className="story-meta">{formatDate(story.added_on)}</div>

      {imgSrc && (
        <>
          <div
            className="story-image-wrapper"
            onClick={() => setShowImagePopup(true)}
          >
            <img
              src={fullImageUrl}
              alt={story.title}
              className="story-image-full"
              onError={onImgError}
            />
            {story.imageText && (
              <div className="story-image-credit">{story.imageText}</div>
            )}
          </div>

          {showImagePopup && (
            <div
              className="image-popup-backdrop"
              onClick={() => setShowImagePopup(false)}
            >
              <img
                src={fullImageUrl}
                alt={story.title}
                className="image-popup-img"
              />
            </div>
          )}
        </>
      )}

      <div
        className="story-body"
        dangerouslySetInnerHTML={{ __html: decodedHtml }}
      />

      {/* Tags under story */}
      {tags.length > 0 && (
  <div className="story-tags-wrapper">
    <div className="story-tags-title">हॅशटॅग :</div>
    <div className="story-tags">
      {tags.map((t, i) => (
        <Link
          key={`${t}-${i}`}
          to={`/hashtag/${encodeURIComponent(t)}`}
          className="news-tag"
        >
          #{t}
        </Link>
      ))}
    </div>
  </div>
)}

      {/* Also read section */}
      {relatedStories.length > 0 && (
        <div className="also-read">
          <h3 className="also-read-title">हेही वाचा !</h3>

          <div className="also-read-list">
            {relatedStories.map((n) => {
              const thumb = n.thumbnail
                ? `${THUMB_BASE_URL}${n.thumbnail}`
                : DEFAULT_THUMBNAIL;

              return (
                <Link
                  key={n.uniq_link}
                  to={`/story/${encodeURIComponent(n.uniq_link)}`}
                  state={{
                    category,
                    relatedStories: relatedStories
                      .filter((x) => x.uniq_link !== n.uniq_link)
                      .slice(0, 6),
                  }}
                  className="also-read-item"
                >
                  <img
                    src={thumb}
                    alt={n.title}
                    className="also-read-thumb"
                    loading="lazy"
                    onError={(e) => (e.currentTarget.src = DEFAULT_THUMBNAIL)}
                  />
                  <div className="also-read-headline">{n.title}</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
};

export default StoryPage;
